import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nexapi from '../../services/api';
import { DashboardModel } from '../../types/models/DashboardModel';
import { useAppContext } from '../../context/appcontext';

interface DashboardState {
  data: DashboardModel[];
  loading: boolean;
  error: string | null;
  totalRecords: number;
  currentPage: number;
  status: string | null;
}
interface DashboardResponse {
  dashboard: DashboardModel[];
  totalCount: number;
}
interface DashboardState {
  totalCounts: {
    myPendingApprovals: number;
    myPendingResubmissions: number;
    myDrafts: number;
    myRequests: number;
    myActionedRequests: number;
    allRequests: number;
    myReviews: number;
  };
  loading: boolean;
  error: string | null;
}
const initialState: DashboardState = {
  data: [],
  totalRecords: 0,
  currentPage: 0,
  loading: false,
  error: null,
  status: 'idle',
  totalCounts: {
    myPendingApprovals: -1,
    myPendingResubmissions: -1,
    myDrafts: -1,
    myRequests: -1,
    myActionedRequests: -1,
    allRequests: -1,
    myReviews: -1,
  },
};
// API URL

const API_URL = '/RequestForm';
export const fetchDashboardPending = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number;userId:string; sortColumn: string; sortOrder: 'asc' | 'desc'; }
>(
  "dashboard/fetchDashboardPending",
  async ({ filters, page, numberOfRecord,userId, sortColumn, sortOrder}) => {
    try {
      
      const response = await nexapi.get(
        `${API_URL}/GetDashboadDataPending?userId=${userId}&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      
      const data = response.data;

      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => {
          // Create an object for approvers with default empty values
          let approverFields: Record<string, string> = {};
          for (let i = 1; i <= 9; i++) {
            approverFields[`_Approver${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.approver)) {
            item.approver.forEach((approverItem) => {
              const seq = approverItem.sequence;
              if (seq >= 1 && seq <= 9) {
                approverFields[`_Approver${seq}`] = approverItem.approverModel?.givenName || "";
              }
            });
          }

          let refrenceFields: Record<string, string> = {};
          for (let i = 1; i <= 12; i++) {
            refrenceFields[`_Refrence${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.refrence)) {
            item.refrence.forEach((refrenceItem) => {
              const seq = refrenceItem.subtypeField?.sequenceNumber;
              if (seq >= 1 && seq <= 12) {
                refrenceFields[`_Refrence${seq}`] = refrenceItem.subtypeField?.value || "";
              }
            });
          }
          const approverRemarks = item.historyLogs
  ? item.historyLogs
      .map((log) => {
        const formattedDate = log.submittedDateTime
          ? new Date(log.submittedDateTime).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            }).replace(",", "")
          : "";
        return `${log.userName} (${formattedDate}) - ${log.remarks}`;
      })
      .join("\n") // Join multiple logs with new line
  : "";

          return {
            Id: item.id,
            RequestId: item.requestID,
            Application: item.applicationTitle,
            Subtype: item.subtypeTitle,
            Requester: item.requestorName,
            _Status: item.formActionString.charAt(0).toUpperCase() + 
            item.formActionString.slice(1) + 
            (item.formActionString.toLowerCase() === "pending" && item?.currentOwnerName?.trim()
              ? " with " + item.currentOwnerName
              : ""),
            _CompanyCode: item.companyName,
            SubmittedDate: item.submittedDateTime
              ? parseUtcDate(item.submittedDateTime).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
               }).replace("Sept", "Sep") // Simple replacement

              : "-",
            _FromDate: item.fromDate
              ? new Date(item.fromDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                }).replace(",", "")
              : "",
            _ToDate: item.toDate
              ? new Date(item.toDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            Subject: item.subject,
            _Reviewer: item.reviewer,
            ...approverFields,
            _Description: item.description,
            ...refrenceFields,
            _Reason: item.reason,
            _RequesterRemark:item.remark,
            _ApproverRemark:approverRemarks,
           // Spread the dynamically generated approvers
          };
        });

        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
   const headerKeys = {
    Id: "",
    RequestId: "",
    Application: "",
    Subtype: "",
    Requester: "",
    SubmittedDate: "",
    Subject: "",
  };

  return {
    dashboard: [headerKeys], // Only headers structure, no data
    totalCount: 0,
  };
        console.error("Invalid API Response:", data);
        //return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);

export const fetchDashboardResubmitted = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number;userId:string; sortColumn: string; sortOrder: 'asc' | 'desc';}
>(
  "dashboard/fetchDashboardResubmitted",
  async ({ filters, page, numberOfRecord,userId, sortColumn, sortOrder }) => {
    try {
      const response = await nexapi.get(
        `${API_URL}/GetDashboadData?userId=${userId}&formAction=${encodeURIComponent("request for amendment")}&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      const data = response.data;
      //console.log(data);
      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => {
          // Create an object for approvers with default empty values
          let approverFields: Record<string, string> = {};
          for (let i = 1; i <= 9; i++) {
            approverFields[`_Approver${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.approver)) {
            item.approver.forEach((approverItem) => {
              const seq = approverItem.sequence;
              if (seq >= 1 && seq <= 9) {
                approverFields[`_Approver${seq}`] = approverItem.approverModel?.givenName || "";
              }
            });
          }

          let refrenceFields: Record<string, string> = {};
          for (let i = 1; i <= 12; i++) {
            refrenceFields[`_Refrence${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.refrence)) {
            item.refrence.forEach((refrenceItem) => {
              const seq = refrenceItem.subtypeField?.sequenceNumber;
              if (seq >= 1 && seq <= 12) {
                refrenceFields[`_Refrence${seq}`] = refrenceItem.subtypeField?.value || "";
              }
            });
          }
          const approverRemarks = item.historyLogs
  ? item.historyLogs
      .map((log) => {
        const formattedDate = log.submittedDateTime
          ? new Date(log.submittedDateTime).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            }).replace(",", "")
          : "";
        return `${log.userName} (${formattedDate}) - ${log.remarks}`;
      })
      .join("\n") // Join multiple logs with new line
  : "";

          return {
            Id: item.id,
            RequestId: item.requestID,
            Application: item.applicationTitle,
            Subtype: item.subtypeTitle,
            _Requester: item.requestorName,
            _Status: item.formActionString.charAt(0).toUpperCase() + 
            item.formActionString.slice(1) + 
            (item.formActionString.toLowerCase() === "pending" && item?.currentOwnerName?.trim()
              ? " with " + item.currentOwnerName
              : ""),
            _CompanyCode: item.companyName,
            SubmittedDate: item.submittedDateTime
              ? parseUtcDate(item.submittedDateTime).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            }).replace("Sept", "Sep") // Simple replacement

              : "-",
            _FromDate: item.fromDate
              ? parseUtcDate(item.fromDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            _ToDate: item.toDate
              ? parseUtcDate(item.toDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            Subject: item.subject,
            _Reviewer: item.reviewer,
            ...approverFields,
            _Description: item.description,
            ...refrenceFields,
            _Reason: item.reason,
            _RequesterRemark:item.remark,
            _ApproverRemark:approverRemarks,
           // Spread the dynamically generated approvers
          };
        });

     
        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        //return { dashboard: [], totalCount: 0 };
        const headerKeys = {
          Id: "",
          RequestId: "",
          Application: "",
          Subtype: "",
          SubmittedDate: "",
          Subject: "",
        };

        return {
          dashboard: [headerKeys], // Only headers structure, no data
          totalCount: 0,
        };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);

export const fetchDashboardDraft = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number;userId:string; sortColumn: string; sortOrder: 'asc' | 'desc'; }
>(
  "dashboard/fetchDashboardDraft",
  async ({ filters, page, numberOfRecord,userId, sortColumn, sortOrder }) => {
    try {
      const response = await nexapi.get(
        `${API_URL}/GetDashboadData?userId=${userId}&formAction=draft&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      const data = response.data;

      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => ({
          Id:item.id,
          RequestId: item.requestID,
          Application: item.applicationTitle,
          Subtype: item.subtypeTitle,
          Subject: item.subject,
        }));

        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        const headerKeys = {
        Id: "",
        RequestId: "",
        Application: "",
        Subtype: "",
        Subject: "",
      };

      return {
        dashboard: [headerKeys], // Only headers structure, no data
        totalCount: 0,
      };
        //return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);


function parseUtcDate(dateString) {
  // If it already ends with Z, treat as UTC
  if (dateString.endsWith("Z")) {
    return new Date(dateString);
  }

  // Otherwise, force treat it as UTC by appending Z
  return new Date(dateString + "Z");
}

export const fetchDashboardRequest = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number;userId:string; sortColumn: string; sortOrder: 'asc' | 'desc'; }
>(
  "dashboard/fetchDashboardRequest",
  async ({ filters, page, numberOfRecord,userId, sortColumn, sortOrder }) => {
    try {
      const response = await nexapi.get(
        `${API_URL}/GetDashboadData?userId=${userId}&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      const data = response.data;

      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => {
          // Create an object for approvers with default empty values
          let approverFields: Record<string, string> = {};
          for (let i = 1; i <= 9; i++) {
            approverFields[`_Approver${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.approver)) {
            item.approver.forEach((approverItem) => {
              const seq = approverItem.sequence;
              if (seq >= 1 && seq <= 9) {
                approverFields[`_Approver${seq}`] = approverItem.approverModel?.givenName || "";
              }
            });
          }

          let refrenceFields: Record<string, string> = {};
          for (let i = 1; i <= 12; i++) {
            refrenceFields[`_Refrence${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.refrence)) {
            item.refrence.forEach((refrenceItem) => {
              const seq = refrenceItem.subtypeField?.sequenceNumber;
              if (seq >= 1 && seq <= 12) {
                refrenceFields[`_Refrence${seq}`] = refrenceItem.subtypeField?.value || "";
              }
            });
          }
          const approverRemarks = item.historyLogs
  ? item.historyLogs
      .map((log) => {
        const formattedDate = log.submittedDateTime
          ? new Date(log.submittedDateTime).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            }).replace(",", "")
          : "";
        return `${log.userName} (${formattedDate}) - ${log.remarks}`;
      })
      .join("\n") // Join multiple logs with new line
  : "";

          return {
            Id: item.id,
            RequestId: item.requestID,
            Application: item.applicationTitle,
            Subtype: item.subtypeTitle,
            _Requester: item.requestorName,
            Status: item.pendingStatus === null ? item.formActionString.charAt(0).toUpperCase() + 
            item.formActionString.slice(1) + 
            (item.formActionString.toLowerCase() === "pending" && item?.currentOwnerName?.trim()
              ? " with " + item.currentOwnerName
              : "") : item.pendingStatus,
            _CompanyCode: item.companyName,
            SubmittedDate: item.submittedDateTime
              ? parseUtcDate(item.submittedDateTime).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
               }).replace("Sept", "Sep") // Simple replacement
              : "-",
            _FromDate: item.fromDate
              ? parseUtcDate(item.fromDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            _ToDate: item.toDate
              ? parseUtcDate(item.toDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            Subject: item.subject,
            _Reviewer: item.reviewer,
            ...approverFields,
            _Description: item.description,
            ...refrenceFields,
            _Reason: item.reason,
            _RequesterRemark:item.remark,
            _ApproverRemark:approverRemarks,
           // Spread the dynamically generated approvers
          };
        });
        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        const headerKeys = {
        Id: "",
        RequestId: "",
        Application: "",
        Subtype: "",
        Status:"",
        SubmittedDate:"",
        Subject: "",
      };

      return {
        dashboard: [headerKeys], // Only headers structure, no data
        totalCount: 0,
      };
        //return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);


export const fetchDashboardReview = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number;userId:string; sortColumn: string; sortOrder: 'asc' | 'desc'; }
>(
  "dashboard/fetchDashboardReview",
  async ({ filters, page, numberOfRecord,userId, sortColumn, sortOrder }) => {
    try {
      const response = await nexapi.get(
        `${API_URL}/GetDashboadDataReview?userId=${userId}&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      const data = response.data;

      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => ({
          Id:item.id,
          RequestId: item.requestID,
          Application: item.applicationTitle,
          Subtype: item.subtypeTitle,
          Requester: item.requestorName,
          Status:item.formActionString?.toLowerCase() === "draft"  
                  ? "Draft"
                  : item.pendingStatus === null
                  ? item.formActionString.charAt(0).toUpperCase() +
                    item.formActionString.slice(1) +
                    (item.formActionString.toLowerCase() === "pending" && item?.currentOwnerName?.trim()
                      ? " with " + item.currentOwnerName
                      : "")
                  : item.pendingStatus,
          SubmittedDate: item.submittedDateTime ? parseUtcDate(item.submittedDateTime).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }).replace("Sept", "Sep") // Simple replacement
          : '-',
          Subject: item.subject,
          _RequesterRemark:item.remark,
        }));

        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        const headerKeys = {
        Id: "",
        RequestId: "",
        Application: "",
        Subtype: "",
        Requester: "",
        Status:"",
        SubmittedDate:"",
        Subject: "",
      };

      return {
        dashboard: [headerKeys], // Only headers structure, no data
        totalCount: 0,
      };
        //return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);

export const fetchDashboardActionedRequest = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number;userId:string; sortColumn: string; sortOrder: 'asc' | 'desc'; }
>(
  "dashboard/fetchDashboardActionedRequest",
  async ({ filters, page, numberOfRecord,userId, sortColumn, sortOrder }) => {
    try {
      const response = await nexapi.get(
        `${API_URL}/GetDashboadDataActioned?userId=${userId}&isActionComplete=true&pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      const data = response.data;

      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => {
          // Create an object for approvers with default empty values
          let approverFields: Record<string, string> = {};
          for (let i = 1; i <= 9; i++) {
            approverFields[`_Approver${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.approver)) {
            item.approver.forEach((approverItem) => {
              const seq = approverItem.sequence;
              if (seq >= 1 && seq <= 9) {
                approverFields[`_Approver${seq}`] = approverItem.approverModel?.givenName || "";
              }
            });
          }

          let refrenceFields: Record<string, string> = {};
          for (let i = 1; i <= 12; i++) {
            refrenceFields[`_Refrence${i}`] = ""; // Prefix with "_" to hide from UI but include in downloads
          }
          
          // Fill in approvers based on sequence
          if (Array.isArray(item.refrence)) {
            item.refrence.forEach((refrenceItem) => {
              const seq = refrenceItem.subtypeField?.sequenceNumber;
              if (seq >= 1 && seq <= 12) {
                refrenceFields[`_Refrence${seq}`] = refrenceItem.subtypeField?.value || "";
              }
            });
          }
          const approverRemarks = item.historyLogs
  ? item.historyLogs
      .map((log) => {
        const formattedDate = log.submittedDateTime
          ? new Date(log.submittedDateTime).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
            }).replace(",", "")
          : "";
        return `${log.userName} (${formattedDate}) - ${log.remarks}`;
      })
      .join("\n") // Join multiple logs with new line
  : "";

          return {
            Id: item.id,
            RequestId: item.requestID,
            Application: item.applicationTitle,
            Subtype: item.subtypeTitle,
            Requester: item.requestorName,
            _CompanyCode: item.companyName,
            Status: item.pendingStatus === null ? item.formActionString.charAt(0).toUpperCase() + 
            item.formActionString.slice(1) + 
            (item.formActionString.toLowerCase() === "pending" && item?.currentOwnerName?.trim()
              ? " with " + item.currentOwnerName
              : "") : item.pendingStatus,
            SubmittedDate: item.submittedDateTime
              ? parseUtcDate(item.submittedDateTime).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                 }).replace("Sept", "Sep") // Simple replacement
              : "-",
            _FromDate: item.fromDate
              ? parseUtcDate(item.fromDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            _ToDate: item.toDate
              ? parseUtcDate(item.toDate).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace(",", "")
              : "",
            Subject: item.subject,
            _Reviewer: item.reviewer,
            ...approverFields,
            _Description: item.description,
            ...refrenceFields,
            _Reason: item.reason,
            _RequesterRemark:item.remark,
            _ApproverRemark:approverRemarks,
           // Spread the dynamically generated approvers
          };
        });

        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        const headerKeys = {
        Id: "",
        RequestId: "",
        Application: "",
        Subtype: "",
        Requester:"",
        Status:"",
        SubmittedDate:"",
        Subject: "",
      };

      return {
        dashboard: [headerKeys], // Only headers structure, no data
        totalCount: 0,
      };
        //return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);



export const fetchDashboardAllRequest = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number; companyId: string; sortColumn: string; sortOrder: 'asc' | 'desc'; }
>(
  "dashboard/fetchDashboardAllRequest",
  async ({ filters, page, numberOfRecord, companyId, sortColumn, sortOrder }) => {
    try {
      const response = await nexapi.get(
        `${API_URL}/GetAllReq?pagination=true&page=${page}&numberOfRecord=${numberOfRecord}&companyId=${companyId}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&userTimeZone=${filters.userTimeZone}`,
        { params: filters }
      );
      const data = response.data;
      
      if (Array.isArray(data?.data)) {

        const filteredData = data.data.map((item) => {
          
          return {
            Id: item.id,
            RequestId: item.requestID,
            Application: item.applicationTitle,
            Subtype: item.subtypeTitle,
            Requester: item.requestorName,
             Status: item.pendingStatus === null ? item.formActionString.charAt(0).toUpperCase() + 
            item.formActionString.slice(1) + 
            (item.formActionString.toLowerCase() === "pending" && item?.currentOwnerName?.trim()
              ? " with " + item.currentOwnerName
              : "") : item.pendingStatus,
            CompanyCode: item.companyCode,
            SubmittedDate: item.submittedDateTime
              ? parseUtcDate(item.submittedDateTime).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
                }).replace("Sept", "Sep") // Simple replacement
              : "-",
            Subject: item.subject,

           // Spread the dynamically generated approvers
          };
        });
        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
         const headerKeys = {
        Id: "",
        RequestId: "",
        Application: "",
        Requester:"",
        Status:"",
        CompanyCode:"",
        SubmittedDate:"",
        Subject: "",
      };

      return {
        dashboard: [headerKeys], // Only headers structure, no data
        totalCount: 0,
      };
        //return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);


export const fetchTotalCountDashboard = createAsyncThunk<
  any, // Replace with a proper response type if available
  { userId: string; companyId: string }
>(
  "dashboard/fetchTotalCountDashboard",
  async ({ userId, companyId }, { rejectWithValue }) => { // ✅ Ensure userId is typed as string
    try {
      const response = await nexapi.get(`${API_URL}/GetTotalCountDashboard?userId=${userId}&companyId=${companyId}`);
      const data = response.data;

      if (typeof data === "object" && data !== null) {
        return {
          myPendingApprovals: data.myPendingApprovals || 0,
          myPendingResubmissions: data.myPendingResubmissions || 0,
          myDrafts: data.myDrafts || 0,
          myRequests: data.myRequests || 0,
          myActionedRequests: data.myActionedRequests || 0,
          allRequests: data.allRequests || 0,
          myReviews: data.myReviews || 0,
        };
      } else {
        console.error("Invalid API Response:", data);
        return rejectWithValue("Invalid API Response");
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      return rejectWithValue(error);
    }
  }
);




export const fetchArchivalJobs = createAsyncThunk<
  DashboardResponse,
  { filters: Record<string, any>; page: number; numberOfRecord: number }
>(
  "dashboard/fetchArchivalJobs",
  async ({ filters, page, numberOfRecord }) => {
    try {
      const response = await nexapi.post(
        `${API_URL}/GetArchivedResults?pagination=true&page=${page}&numberOfRecord=${numberOfRecord}`,
        { params: filters }
      );
      const data = response.data;

      if (Array.isArray(data?.data)) {
        const filteredData = data.data.map((item) => ({
          historyId:item.historyId,
          status: item.status,
          archivalStartDate: item.archivalStartDate? new Date(item.archivalStartDate).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }).replace(",", "")
        : "",
          archivalEndDate: item.archivalEndDate? new Date(item.archivalEndDate).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }).replace(",", "")
        : "",
          failureReason:item?.failureReason || 'NA',
          startTime: item.startTime,
          endTime: item.endTime,
        }));

        return {
          dashboard: filteredData,
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        return { dashboard: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      throw error;
    }
  }
);




const requestsSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardPending.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardPending.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardPending.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      .addCase(fetchDashboardDraft.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardDraft.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      .addCase(fetchDashboardResubmitted.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardResubmitted.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardResubmitted.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      .addCase(fetchDashboardRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardRequest.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      .addCase(fetchDashboardReview.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardReview.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardReview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      .addCase(fetchDashboardActionedRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardActionedRequest.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardActionedRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      .addCase(fetchDashboardAllRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDashboardAllRequest.fulfilled, (state, action) => {
        state.data = action.payload.dashboard;
        state.totalRecords = action.payload.totalCount;
        state.status = 'succeeded';
      })
      .addCase(fetchDashboardAllRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      }).addCase(fetchTotalCountDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchTotalCountDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.totalCounts = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchTotalCountDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.status = 'failed';
      })
      .addCase(fetchArchivalJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchArchivalJobs.fulfilled, (state, action) => {
        state.data = action.payload.dashboard; 
        state.totalRecords = action.payload.totalCount;
        state.status = "succeeded";
        state.loading = false;
    })
      .addCase(fetchArchivalJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard';
      })
      
  },
});

export default requestsSlice.reducer;